import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import PageHeader from "@/components/PageHeader";
import RequestCoachingButton from "@/components/RequestCoachingButton";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { MessagesSquare, Sparkles } from "lucide-react";
import {
  MANUAL_TITLE,
  MANUAL_SUBTITLE,
  MANUAL_MARKDOWN,
} from "@/lib/manual-content";

// Split point: just before exercise 2 starts. The GOT-practice CTA is
// inserted here, so users can rehearse the just-read Gevoelsomdraaitechniek
// with the AI before moving on to the gedachtenwereld training.
const EX2_HEADING = "### 2. Het trainen van je gedachtenwereld";

function splitAtExercise2(markdown) {
  const idx = markdown.indexOf(EX2_HEADING);
  if (idx === -1) return [markdown, ""];
  return [markdown.slice(0, idx), markdown.slice(idx)];
}

export default function Manual() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const [before, after] = useMemo(
    () => splitAtExercise2(MANUAL_MARKDOWN),
    []
  );

  return (
    <div
      className="px-8 lg:px-12 py-12 max-w-4xl mx-auto"
      data-testid="manual-page"
    >
      <PageHeader
        overline={t("manual.overline")}
        title={MANUAL_TITLE}
        description={MANUAL_SUBTITLE}
      />

      <article
        className="manual-prose bg-white border border-[#E5E2DC] rounded-2xl px-8 sm:px-12 py-10 sm:py-14"
        data-testid="manual-content"
      >
        <ReactMarkdown>{before}</ReactMarkdown>

        {/* GOT practice CTA — placed after exercise 1, before exercise 2 */}
        <aside
          className="not-prose my-10 rounded-2xl border border-[#E5D2C2] bg-gradient-to-br from-[#F9F5F0] to-[#F2EAE0] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5"
          data-testid="manual-got-cta"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5D2C2]">
              <MessagesSquare
                strokeWidth={1.5}
                className="text-[#B37A5F]"
                size={20}
              />
            </div>
            <div>
              <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                {t("manual.cta.title")}
              </h3>
              <p className="text-sm text-[#5C635A] mt-1 leading-relaxed max-w-xl">
                {t("manual.cta.body")}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/me/got-practice")}
            data-testid="manual-got-practice-button"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white shrink-0"
          >
            <Sparkles strokeWidth={1.5} size={16} className="mr-2" />
            {t("manual.cta.button")}
          </Button>
        </aside>

        <ReactMarkdown>{after}</ReactMarkdown>

        {/* Request coaching CTA — shown to prospects/trainees only (the
            component auto-hides for coaches and existing coachees). Placed
            after the Conclusie so it reads as a natural follow-up. */}
        <div className="not-prose mt-6">
          <p
            className="text-sm text-[#5C635A] leading-relaxed"
            data-testid="manual-coach-pitch"
          >
            {t("manual.coach_pitch")}
          </p>
          <RequestCoachingButton clientType={user?.client_type} />
        </div>
      </article>

      <p
        className="text-xs text-[#8A9188] mt-6 leading-relaxed text-center"
        data-testid="manual-copyright"
      >
        {t("footer.copyright_prefix")}{" "}
        <a
          href="https://www.neurosofie.nl/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline-offset-2 hover:underline hover:text-[#1A1D1A]"
          data-testid="manual-copyright-link"
        >
          {t("footer.copyright_org")}
        </a>
        {t("footer.copyright_address")}
      </p>
    </div>
  );
}
